import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FileUploader } from 'ng2-file-upload';

import { FileUploaderService } from '../file-uploader.service';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import environment from '../../../environments/environment';

const ATTACHMENT_ICONS: Record<string, string> = {
    pdf: 'ph-file-pdf', doc: 'ph-file-doc', docx: 'ph-file-doc', xls: 'ph-file-xls', xlsx: 'ph-file-xls',
    csv: 'ph-file-csv', txt: 'ph-file-txt', png: 'ph-file-image', jpg: 'ph-file-image', jpeg: 'ph-file-image',
    gif: 'ph-file-image', webp: 'ph-file-image',
};

export function attachmentIconClass(fileName: string | null | undefined): string {
    const extension = (fileName || '').toLowerCase().split('.').pop() || '';
    return ATTACHMENT_ICONS[extension] || 'ph-file';
}

export interface GableAttachmentUploadResult {
    uploadedFileId: number;
    fileName: string;
}

export interface GableAttachmentUploadError {
    message: string;
    status?: number;
}

/** Matches core GableUploadedFiles::ALLOWED_EXTENSIONS. */
export const ALLOWED_ATTACHMENT_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'];

/** Uploads one Gable drawer attachment through /file/upload and returns its uploadedFileId. */
@Injectable({
    providedIn: 'root',
})
export class GableAttachmentUploaderService {
    constructor(private _fileUploaderService: FileUploaderService) {}

    uploadFile(file: File): Observable<GableAttachmentUploadResult> {
        const jwt = LegFiJwtService.read();
        if (!jwt) {
            return new Observable(subscriber => {
                subscriber.error({ message: 'Session expired. Please refresh and try again.' } as GableAttachmentUploadError);
            });
        }

        const validationError = this.validateFile(file);
        if (validationError) {
            return new Observable(subscriber => {
                subscriber.error({ message: validationError } as GableAttachmentUploadError);
            });
        }

        // One uploader per file, so several attachments can upload at once without sharing a queue.
        const subject = new Subject<GableAttachmentUploadResult>();
        const uploader = this._createUploader(subject, file.name);
        uploader.addToQueue([file]);
        uploader.uploadAll();

        return subject.asObservable();
    }

    validateFile(file: File): string | null {
        const fileName = file.name.trim().toLowerCase();
        const extension = fileName.includes('.') ? fileName.split('.').pop() : '';

        if (!extension || !ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension)) {
            return `Gable can take ${ALLOWED_ATTACHMENT_EXTENSIONS.join(', ')} files.`;
        }

        if (file.size > environment.WorkerServerFileSizeLimit) {
            const limitMb = Math.ceil((environment.WorkerServerFileSizeLimit / 1024) / 1024);
            return `File exceeds the ${limitMb}MB limit.`;
        }

        return null;
    }

    getAcceptedMimeTypes(): string {
        return ALLOWED_ATTACHMENT_EXTENSIONS.map(ext => `.${ext}`).join(',');
    }

    private _createUploader(subject: Subject<GableAttachmentUploadResult>, fileName: string): FileUploader {
        const uploader = this._fileUploaderService.initUploader({
            url: Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile()),
        });

        uploader.options.autoUpload = false;

        uploader.onBuildItemForm = (fileItem: any, form: any) => {
            form.append('fileName', fileItem.file.name);
        };

        uploader.onCompleteItem = (item, response, status) => {
            if (status >= 200 && status < 300) {
                try {
                    const res = JSON.parse(response);
                    if (res.id) {
                        subject.next({ uploadedFileId: res.id, fileName });
                        subject.complete();
                    } else {
                        subject.error({ message: 'Upload succeeded but no file ID returned.', status } as GableAttachmentUploadError);
                    }
                } catch (e) {
                    subject.error({ message: 'Failed to parse upload response.', status } as GableAttachmentUploadError);
                }
            }
        };

        uploader.onErrorItem = (fileItem: any, response: string, status: number) => {
            let errorMessage = 'Failed to upload file. Please try again.';

            if (response) {
                try {
                    const errorResponse = JSON.parse(response);
                    errorMessage = errorResponse['error'] || errorMessage;
                } catch (e) {
                    // use default
                }
            }

            subject.error({ message: errorMessage, status } as GableAttachmentUploadError);
        };

        return uploader;
    }
}
